import React, {
	FC,
	useMemo,
	useState,
	useEffect,
	useCallback,
	useRef
} from 'react';
import TextInputLaptop from "../atomicDesignLaptop/TextInputLaptop";
import CountrySelectInputLaptop from "../atomicDesignLaptop/CountrySelectInputLaptop";
import allCountriesList from "../../../utils/constants/allCountriesList";
import { useTranslation } from "react-i18next";
import CompaniesService from "../../../services/Companies/CompaniesService";
import toTitleCase from "../../../utils/functions/toTitleCase";
import formatSireXNumber from "../../../utils/functions/formatSireXNumber";
import removeSpaces from "../../../utils/functions/removeSpaces";
import formatNBTVA from "../../../utils/functions/formatNBTVA";
import axios from 'axios';
import AzureMapsService from "../../../services/AzureMaps/AzureMapsService";
import { useAuthContext } from "../../../contexts/AuthContext";
import { CompanyLookupTelemetry } from '../../../types';

const SignUpPageLaptopProPanel2: FC = () => {
	const {
		userInfo,
		setUserInfo,
		isSecondPanelValidated,
		validateSecondPanel,
		syncExistingCompanyMatchByVAT,
		activateExistingCompanyAttachment,
		openedPanel,
		setOpenedPanel
	} = useAuthContext();
	const { t, i18n } = useTranslation();
	const [fetchingCompanyData, setFetchingCompanyData] = useState(false);
	const [showClassicCompanyForm, setShowClassicCompanyForm] = useState(false);
	const [disableFields, setDisableFields] = useState({
		nbtva: false,
		companyName: false,
	});
	const [wrongFields, setWrongFields] = useState({
		siret: false,
		siren: false,
		postalCode: false,
		nbtva: false,
		city: false,
		address: false,
		companyName: false,
	});
	const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
	const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
	const autocompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lookupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const isFR = userInfo.company.countryCode === 'FR';
	const identifierValue = isFR ? userInfo.company.siret : userInfo.company.nbtva;
	const isReducedLookupMode = !showClassicCompanyForm;
	const lookupTelemetry = useMemo<CompanyLookupTelemetry>(
		() => ({
			lookupMode: isReducedLookupMode ? 'reduced' : 'classic',
			countryCode: userInfo.company.countryCode,
			identifierType: 'vat',
		}),
		[isReducedLookupMode, userInfo.company.countryCode]
	);

	useEffect(() => {
		const cleanIdentifier = removeSpaces(identifierValue);

		if (lookupTimeoutRef.current) {
			clearTimeout(lookupTimeoutRef.current);
		}

		if (!cleanIdentifier) {
			setShowClassicCompanyForm(false);
			setDisableFields({
				nbtva: false,
				companyName: false,
			});
				void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
			return;
		}

		const isLookupReady = isFR ? cleanIdentifier.length === 14 : cleanIdentifier.length >= 8;
		if (!isLookupReady) {
			setShowClassicCompanyForm(false);
			setDisableFields({
				nbtva: false,
				companyName: false,
			});
				void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
			return;
		}

		lookupTimeoutRef.current = setTimeout(() => {
			const fetchCompanyData = async () => {
				setFetchingCompanyData(true);
				try {
					if (isFR) {
						const { data } = await CompaniesService.sirenInformationRead(cleanIdentifier.substring(0, 9));
						if (data.denominationUniteLegale === null || data.denominationUniteLegale.includes("[ND] [ND]")) {
							setDisableFields({
								nbtva: false,
								companyName: false,
							});
								await syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
							setShowClassicCompanyForm(true);
							return;
						}
						setDisableFields({
							nbtva: true,
							companyName: true,
						});
						setUserInfo((prevState: any) => ({
							...prevState,
							company: {
								...prevState.company,
								siret: data.siret || prevState.company.siret,
								siren: data.siren || prevState.company.siren,
								companyName: toTitleCase(data.denominationUniteLegale) || prevState.company.companyName,
								nbtva: data.tva || prevState.company.nbtva,
							},
						}));
						const matchedCompany = await syncExistingCompanyMatchByVAT(data.tva, lookupTelemetry);
						if (matchedCompany) {
							activateExistingCompanyAttachment(matchedCompany);
							return;
						}
						setShowClassicCompanyForm(true);
						return;
					}

					setDisableFields({
						nbtva: false,
						companyName: false,
					});
					const matchedCompany = await syncExistingCompanyMatchByVAT(cleanIdentifier, lookupTelemetry);
					if (matchedCompany) {
						activateExistingCompanyAttachment(matchedCompany);
						return;
					}
					setShowClassicCompanyForm(true);
				} catch (error) {
					console.error("Erreur lors de la récupération des informations de l'entreprise", error);
					setDisableFields({
						nbtva: false,
						companyName: false,
					});
					setShowClassicCompanyForm(true);
				} finally {
					setFetchingCompanyData(false);
				}
			};

			void fetchCompanyData();
		}, 350);

		return () => {
			if (lookupTimeoutRef.current) {
				clearTimeout(lookupTimeoutRef.current);
			}
		};
	}, [activateExistingCompanyAttachment, identifierValue, isFR, lookupTelemetry, setUserInfo, syncExistingCompanyMatchByVAT]);

	useEffect(() => {
		if (!userInfo.company.countryCode) {
			setUserInfo((prevState: any) => ({
				...prevState,
				company: {
					...prevState.company,
					countryCode: 'FR',
				},
			}));
		}
	}, [setUserInfo, userInfo.company.countryCode]);

	const validateFields = () => {
		const newWrongFields = {
			siret: (isFR && userInfo.company.siret.length !== 14) || false,
			siren: (isFR && userInfo.company.siren.length !== 9) || false,
			postalCode:
				(isFR && userInfo.company.postalCode.length !== 5) ||
				userInfo.company.postalCode === "",
			nbtva: userInfo.company.nbtva === "",
			city: userInfo.company.city === "",
			address: userInfo.company.address === "",
			companyName: userInfo.company.companyName === "",
		};

		setWrongFields(newWrongFields);

		return Object.values(newWrongFields).every((value) => !value);
	};

	const handleAddressAutocomplete = useCallback(
		async (query: string) => {
			const source = axios.CancelToken.source();

			try {
				const response = await AzureMapsService.getAddressSuggestions(query, {
					cancelToken: source.token,
				});
				const suggestions = response.data.results.map((result: any) => ({
					label: result.address.freeformAddress,
					city: result.address.municipality,
					postalCode: result.address.postalCode,
				}));
				setAddressSuggestions(suggestions);
				setShowAddressSuggestions(true);
			} catch (error: any) {
				if (axios.isCancel(error)) {
					console.log("Request canceled", error.message);
				} else {
					console.error(
						"Erreur lors de la récupération des suggestions d'adresse",
						error
					);
				}
			}
		},
		[]
	);

	const handleAddressChange = (e: any) => {
		const newAddress = e.target.value;

		setUserInfo((prevState: any) => ({
			...prevState,
			company: {
				...prevState.company,
				address: newAddress,
			},
		}));

		if (autocompleteTimeoutRef.current) {
			clearTimeout(autocompleteTimeoutRef.current);
		}

		autocompleteTimeoutRef.current = setTimeout(() => {
			handleAddressAutocomplete(newAddress);
		}, 400);
	};

	const handleSelectSuggestion = (suggestion: any) => {
		setShowAddressSuggestions(false);
		const [street] = suggestion.label.split(",");
		setUserInfo((prevState: any) => ({
			...prevState,
			company: {
				...prevState.company,
				address: street.trim(),
				city: suggestion.city,
				postalCode: suggestion.postalCode,
			},
		}));
	};

	const blockAddress = useMemo(() => {
		return (
			<>
				<TextInputLaptop
					width={"100%"}
					type="text"
					value={userInfo.company.address}
					placeholder="6 rue des Lys"
					title={t("secondPanel:address")}
					mandatory
					onChange={handleAddressChange}
					wrong={wrongFields.address}
					wrongMessage={t("secondPanel:fill_this_field")}
				/>
				{showAddressSuggestions && (
					<div
						style={{
							position: "absolute",
							width: "100%",
							backgroundColor: "#fff",
							border: "1px solid #ccc",
							maxHeight: 160,
							overflowY: "auto",
							zIndex: 10,
						}}
					>
						{addressSuggestions.map((suggestion: any, index: number) => (
							<div
								key={index}
								onClick={() => handleSelectSuggestion(suggestion)}
								style={{
									padding: 10,
									cursor: "pointer",
									borderBottom: "1px solid #eee",
								}}
							>
								{suggestion.label}
							</div>
						))}
					</div>
				)}
			</>
		);
	}, [
		userInfo.company.address,
		wrongFields.address,
		addressSuggestions,
		showAddressSuggestions,
		t,
	]);

	const blockCity = useMemo(() => {
		return (
			<TextInputLaptop
				width={"100%"}
				type="text"
				value={userInfo.company.city}
				placeholder="Grenoble"
				title={t("secondPanel:city")}
				mandatory
				onChange={(e) =>
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							city: toTitleCase(e.target.value),
						},
					}))
				}
				wrong={wrongFields.city}
				wrongMessage={t("secondPanel:fill_this_field")}
			/>
		);
	}, [userInfo.company.city, wrongFields.city, setUserInfo, t]);

	const blockCompanyName = useMemo(() => {
		return (
			<TextInputLaptop
				width={"100%"}
				type="text"
				value={userInfo.company.companyName}
				placeholder="Garage Dupont"
				title={t("secondPanel:company_name")}
				mandatory
				disabled={disableFields.companyName}
				loading={fetchingCompanyData}
				onChange={(e) =>
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							companyName: toTitleCase(e.target.value),
						},
					}))
				}
				wrong={wrongFields.companyName}
				wrongMessage={t("secondPanel:fill_this_field")}
			/>
		);
	}, [
		userInfo.company.companyName,
		fetchingCompanyData,
		wrongFields.companyName,
		disableFields.companyName,
		setUserInfo,
		t,
	]);

	const blockNbtva = useMemo(() => {
		return (
			<TextInputLaptop
				width={"100%"}
				type="text"
				value={formatNBTVA(userInfo.company.nbtva)}
				placeholder="FR 12 123456789"
				title={t("secondPanel:vat_number")}
				mandatory
				disabled={disableFields.nbtva}
				loading={fetchingCompanyData}
				onChange={(e) =>
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							nbtva: removeSpaces(e.target.value).toUpperCase(),
						},
					}))
				}
				wrong={wrongFields.nbtva}
				wrongMessage={t("secondPanel:fill_this_field")}
			/>
		);
	}, [
		userInfo.company.nbtva,
		fetchingCompanyData,
		wrongFields.nbtva,
		disableFields.nbtva,
		setUserInfo,
		t,
	]);

	const blockPostalCode = useMemo(() => {
		return (
			<TextInputLaptop
				width={"100%"}
				type="text"
				value={userInfo.company.postalCode}
				placeholder="38000"
				title={t("secondPanel:postal_code")}
				mandatory
				onChange={(e) => {
					const newValue = e.target.value;
					if (
						(userInfo.company.countryCode === "FR" &&
							/^\d*$/.test(newValue)) ||
						userInfo.company.countryCode !== "FR"
					) {
						setUserInfo((prevState: any) => ({
							...prevState,
							company: {
								...prevState.company,
								postalCode: newValue,
							},
						}));
					}
				}}
				wrong={wrongFields.postalCode}
				wrongMessage={t("secondPanel:wrong_postal_code")}
			/>
		);
	}, [
		userInfo.company.postalCode,
		userInfo.company.countryCode,
		wrongFields.postalCode,
		setUserInfo,
		t,
	]);

	const blockReceiverRecommendations = useMemo(() => {
		return <div style={{ width: '100%' }}/>;
	}, []);

	const blockSiren = useMemo(() => {
		return (
			<TextInputLaptop
				width={"100%"}
				type="text"
				value={formatSireXNumber(userInfo.company.siren)}
				placeholder="123 456 789"
				title={t("secondPanel:siren")}
				mandatory
				disabled
				wrong={wrongFields.siren}
				wrongMessage={t("secondPanel:wrong_siren")}
			/>
		);
	}, [userInfo.company.siren, wrongFields.siren, t]);

	const blockIdentifier = useMemo(() => {
		if (isFR) {
			return (
				<TextInputLaptop
					width={"100%"}
					type="text"
					value={formatSireXNumber(userInfo.company.siret)}
					placeholder="123 456 789 00001"
					title={t("secondPanel:siret")}
					mandatory
					loading={fetchingCompanyData}
					onChange={(e) => {
						const newValue = removeSpaces(e.target.value);
						if (/^\d*$/.test(newValue)) {
							if (newValue.length !== 14) {
								void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
							}
							setUserInfo((prevState: any) => ({
								...prevState,
								company: {
									...prevState.company,
									siret: newValue,
									siren:
										newValue.length === 14 ? newValue.substring(0, 9) : "",
									nbtva: "",
								},
							}));
						}
					}}
					wrong={wrongFields.siret}
					wrongMessage={t("secondPanel:wrong_siret")}
				/>
			);
		}

		return (
			<TextInputLaptop
				width={"100%"}
				type="text"
				value={formatNBTVA(userInfo.company.nbtva)}
				placeholder="FR 12 123456789"
				title={t("secondPanel:vat_number")}
				mandatory
				loading={fetchingCompanyData}
				onChange={(e) => {
					const newValue = removeSpaces(e.target.value).toUpperCase();
					setShowClassicCompanyForm(false);
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							nbtva: newValue,
							siret: "",
							siren: "",
						},
					}));
				}}
				wrong={wrongFields.nbtva}
				wrongMessage={t("secondPanel:fill_this_field")}
			/>
		);
	}, [
		fetchingCompanyData,
		isFR,
		lookupTelemetry,
		syncExistingCompanyMatchByVAT,
		userInfo.company.nbtva,
		userInfo.company.siret,
		wrongFields.nbtva,
		wrongFields.siret,
		setUserInfo,
		t,
	]);

	return (
		<div
			style={{
				marginTop: 20,
				width: "90%",
				height: openedPanel === "company information" ? "auto" : 30,
				background: "#FFFFFF",
				borderRadius: 20,
				position: "relative",
				padding: 24,
				boxShadow: "0px 1px 6px #00000029",
			}}
		>
			{/* HEADER */}
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "space-between",
					gap: 2,
					alignItems: "center",
					cursor: "pointer",
				}}
				onClick={() =>
					setOpenedPanel(
						openedPanel === "company information" ? "" : "company information"
					)
				}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "row",
						gap: 8,
					}}
				>
					{isSecondPanelValidated ? (
						<img
							src="/images/check-2.svg"
							alt="check"
							style={{
								width: 26,
								height: 26,
								marginBottom: -3,
								marginLeft: -8,
							}}
						/>
					) : (
						<div style={{ width: 18 }} />
					)}
					<div
						style={{
							fontSize: 25,
							fontWeight: "bold",
							display: "flex",
							alignItems: "center",
						}}
					>
						{t("secondPanel:company_info")}
					</div>
				</div>
				{openedPanel === "company information" ? (
					<img
						src="/images/chevron-down.svg"
						alt="chevron-down"
						style={{ width: 20, height: 20 }}
					/>
				) : (
					<img
						src="/images/chevron-right.svg"
						alt="chevron-right"
						style={{ width: 24, height: 24 }}
					/>
				)}
			</div>

			{openedPanel === "company information" && (
				<>
					{isReducedLookupMode ? (
						<>
							<div
								style={{
									margin: 20,
									display: "flex",
									flexDirection: "row",
									alignItems: "flex-start",
									gap: 24,
								}}
							>
								<div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
									<CountrySelectInputLaptop
										width={"100%"}
										value={userInfo.company.countryCode}
										options={allCountriesList}
										title={t("secondPanel:country")}
										mandatory
										currentLanguage={i18n.language}
										onChange={(e) => {
											const selectedCountry = e.target.value;
											setUserInfo((prevState: any) => ({
												...prevState,
												company: {
													...prevState.company,
													countryCode: selectedCountry,
													siret: "",
													siren: "",
													companyName: "",
													nbtva: "",
													address: "",
													postalCode: "",
													city: "",
												},
											}));
											void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
										}}
									/>
								</div>
								<div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
								{blockIdentifier}
								</div>
							</div>
							<div
								style={{
									margin: '6px 20px 0 20px',
									borderTop: '1px dashed #E6E8EF',
									paddingTop: 18,
									color: '#A0A7B6',
									fontSize: 14,
									fontStyle: 'italic',
								}}
							>
								{t("secondPanel:company_fields_hint")}
							</div>
						</>
					) : (
						<div
							style={{
								margin: 20,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "flex-start",
								gap: 8,
							}}
						>
							{/* Colonne 1 */}
							<div
								style={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									gap: 16,
									width: '30%'
								}}
							>
								<CountrySelectInputLaptop
									width={"100%"}
									value={userInfo.company.countryCode}
									options={allCountriesList}
									title={t("secondPanel:country")}
									mandatory
									currentLanguage={i18n.language}
									onChange={(e) => {
										const selectedCountry = e.target.value;
										setUserInfo((prevState: any) => ({
											...prevState,
											company: {
												...prevState.company,
												countryCode: selectedCountry,
												siret: "",
												siren: "",
												companyName: "",
												nbtva: "",
												address: "",
												postalCode: "",
												city: "",
											},
										}));
											void syncExistingCompanyMatchByVAT(undefined, lookupTelemetry);
									}}
								/>

								{isFR ? blockIdentifier : blockNbtva}
								{isFR ? blockNbtva : blockCompanyName}
							</div>

							{/* Colonne 2 (position: relative pour l'autocomplete d'adresse) */}
							<div
								style={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									gap: 16,
									width: '30%'

								}}
							>
								{isFR ? blockSiren : blockAddress}
								{isFR ? blockCompanyName : blockCity}
								{isFR ? blockAddress : blockPostalCode}
							</div>

							{/* Colonne 3 */}
							<div
								style={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									gap: 16,
									width: '30%'

								}}
							>
								{isFR ? blockCity : blockReceiverRecommendations}
								{isFR && blockPostalCode}
								{isFR && blockReceiverRecommendations}
							</div>
						</div>
					)}

					{!isReducedLookupMode && (
						<div
							style={{
								margin: 20,
								display: 'flex',
								alignItems:'end',
								justifyContent: 'end',
								gap: 8
							}}
						>
						<button
							style={{
								padding: "0px 25px",
								height: 43,
								borderRadius: 10,
								backgroundColor: "#FF4D00",
								color: "#FFFFFF",
								border: "none",
								fontSize: 19,
								fontWeight: "bold",
								cursor: "pointer",
								textTransform: "uppercase",
							}}
							onClick={() => {
								if (validateFields()) {
									validateSecondPanel();
								}
							}}
						>
							{t("secondPanel:validate_button")}
						</button>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default SignUpPageLaptopProPanel2;
